import Image from 'next/future/image';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AppCtx } from 'pages/_app';
import { useRouter } from 'next/router';
import axios from 'axios';
import { SearchOffers } from '@lambda/commons/apis/elasticsearch/types';
import buyAgain from '@/public/icons/account/buy-again.svg';
import useCart, {
  generateAddCartDateTimeAttr,
  generateCategoryAttr,
} from '@/lib/use-cart';
import useDeliveryDate from '@/lib/use-delivery-date';
import useOfferUppricerMeta from '@/lib/use-offer-uppricer-meta';
import settings from '@/settings';
import { findItemInCart } from '../products/PurchaseLinks';

function BuyAgain({
  variantId,
  category,
  vendor,
  productId,
}: {
  variantId: number | undefined;
  productId: number | undefined;
  category: string;
  vendor: string;
}) {
  const shouldRedirect = !!variantId;
  const router = useRouter();
  const { pathname } = router;
  const [started, setStarted] = useState<null | 'add'>(null);
  const [isAvailableForSale, setIsAvailableForSale] = useState<
    boolean | undefined
  >(false);
  const [sku, setSku] = useState<string | undefined>(undefined);
  const { zipcodeStateData, cart: currentCart } = useContext(AppCtx);
  const cart = useCart();
  const isCart = pathname.endsWith('/cart');
  const offerUppricerMeta = useOfferUppricerMeta({ vendor, sku });
  const deliveryDate = useDeliveryDate({
    zipcode: zipcodeStateData?.zipcode,
    state: zipcodeStateData?.state,
    category,
    vendor,
  });
  const shippingData = {
    type: 'standard',
    value: 0,
    cutoffTime: deliveryDate?.standard.cutoffTime || '',
    cutoffTimezone: deliveryDate?.standard.cutoffTimezone || '',
  };

  const deliverBy = {
    min: deliveryDate?.standard.minDeliveryDate || '',
    max: deliveryDate?.standard.maxDeliveryDate || '',
  };

  const useSubmit = useCallback(async () => {
    setStarted('add');

    const shopifyVariantId = `gid://shopify/ProductVariant/${variantId}`;

    const itemInCart = findItemInCart(shopifyVariantId, currentCart);

    const offerUppricerMetaAttributes = [
      offerUppricerMeta?.upPricerRule != null && {
        key: '_uppricer',
        value: offerUppricerMeta.upPricerRule,
      },
      offerUppricerMeta?.upPriceAmount != null && {
        key: '_uppricer_total_amount',
        value: offerUppricerMeta.upPriceAmount.toString(),
      },
      offerUppricerMeta?.priceBeforeUpPricer != null && {
        key: '_uppricer_pre_upprice',
        value: offerUppricerMeta.priceBeforeUpPricer.toString(),
      },
      offerUppricerMeta?.priceAfterUpPricer != null && {
        key: '_uppricer_post_upprice',
        value: offerUppricerMeta.priceAfterUpPricer.toString(),
      },
    ].filter(Boolean) as { key: string; value: string }[];

    const shippingAttributes = [
      { key: '_shipping_option', value: shippingData.type },
      {
        key: '_shipping_option_cost',
        value: shippingData.value.toString(),
      },
    ];

    const deliverByAttributes =
      deliverBy && deliverBy.min !== ''
        ? [
            {
              key: '_deliver_min',
              value: deliverBy.min,
            },
            {
              key: '_deliver_max',
              value: deliverBy.max,
            },
            {
              key: '_cutoff_time',
              value:
                shippingData.cutoffTime === '' ||
                shippingData.cutoffTime == null
                  ? ''
                  : shippingData.cutoffTime,
            },
            {
              key: '_cutoff_timezone',
              value:
                shippingData.cutoffTimezone === '' ||
                shippingData.cutoffTimezone == null
                  ? ''
                  : shippingData.cutoffTimezone,
            },
            ...(['reebelo-au', 'reebelo-nz', 'reebelo-ca'].includes(
              settings.store,
            )
              ? [
                  {
                    key: '_deliver_state',
                    value: zipcodeStateData?.state.toString() || '',
                  },
                  {
                    key: '_deliver_zipcode',
                    value: zipcodeStateData?.zipcode.toString() || '',
                  },
                ]
              : []),
          ]
        : [];

    const cartId = await cart.getOrCreateCartId();

    if (itemInCart == null && isAvailableForSale) {
      await cart.add(
        {
          merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
          attributes: [
            ...deliverByAttributes,
            ...generateAddCartDateTimeAttr(),
            ...generateCategoryAttr(category),
            ...offerUppricerMetaAttributes,
          ],
        },
        cartId,
      );
    } else if (isAvailableForSale && itemInCart != null) {
      await cart.update(
        [
          {
            attributes: [
              ...deliverByAttributes,
              ...shippingAttributes,
              ...offerUppricerMetaAttributes,
            ],
            id: itemInCart.id,
            quantity: itemInCart.quantity + 1,
          },
        ],
        cartId,
      );
    }

    if (shouldRedirect) {
      if (isCart) window.location.href = '/cart';

      return router.push('/cart');
    }

    return null;
  }, [variantId, isAvailableForSale]);

  useEffect(() => {
    // after 3 seconds reset the started state to avoid infinite loading on cached page state.
    if (started) setTimeout(() => setStarted(null), 3000);
  }, [started]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await axios.get(
          `/api/accounts/orders/buyagain?productId=${productId}&variantId=${variantId}`,
        );

        const variant = data?.offer as SearchOffers | null;

        setSku(variant?.sku);
        setIsAvailableForSale((variant?.stock ?? 0) > 0);
      } catch (error) {
        setSku(undefined);
        setIsAvailableForSale(false);
      }
    };

    fetchProduct();
  }, [productId, variantId]); // Added dependencies to re-fetch when productId or variantId changes

  const submit = useSubmit;

  if (!isAvailableForSale) return null;

  return (
    <div
      className="flex w-full cursor-pointer items-center justify-center"
      onClick={() => submit()}
    >
      <div className="mr-1 flex h-[20px] w-[20px] items-center justify-center">
        <Image width={20} height={20} alt="buy again" src={buyAgain} />
      </div>
      <span className="w-full pt-1">Buy it again</span>
    </div>
  );
}

export default BuyAgain;
